import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["onChange", "onValueChange", "value"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback, useMemo, useState } from 'react';
import { useSafeInit, useSingleton, useRefCallback } from '@tencent/glue-react-use';
import { ColorStraw, ColorStrawOperate } from '@tencent/xj-color-straw';
import { Modal } from '../../Modal';
import { usePrefixCls } from '../../../config/hooks/usePrefixCls';
import ColorWrap from 'react-color/lib/components/common/ColorWrap';
import SaturationComponent from 'react-color/lib/components/common/Saturation';
import HueComponent from 'react-color/lib/components/common/Hue';
import AlphaComponent from 'react-color/lib/components/common/Alpha';
import Checkboard from 'react-color/lib/components/common/Checkboard';
import SketchFields from 'react-color/lib/components/sketch/SketchFields';
import SketchPresetColorsComponent from 'react-color/lib/components/sketch/SketchPresetColors';
var Hue = HueComponent;
var Alpha = AlphaComponent;
var Saturation = SaturationComponent;
var SketchPresetColors = SketchPresetColorsComponent;
export var DEFAULT_COLORS = ['#D0021B', '#F5A623', '#F8E71C', '#8B572A', '#7ED321', '#417505', '#BD10E0', '#9013FE', '#4A90E2', '#50E3C2', '#B8E986', '#000', '#4A4A4A', '#9B9B9B', '#FFF'];
export var ColorPickerPanelImpl = ColorWrap(function (props) {
  var _props$width = props.width,
    width = _props$width === void 0 ? 200 : _props$width,
    rgb = props.rgb,
    hex = props.hex,
    hsv = props.hsv,
    hsl = props.hsl,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    onSwatchHover = props.onSwatchHover,
    _props$enableAlpha = props.enableAlpha,
    enableAlpha = _props$enableAlpha === void 0 ? true : _props$enableAlpha,
    _props$enableChromePl = props.enableChromePlugin,
    enableChromePlugin = _props$enableChromePl === void 0 ? true : _props$enableChromePl,
    _props$presetColors = props.presetColors,
    presetColors = _props$presetColors === void 0 ? DEFAULT_COLORS : _props$presetColors,
    onTrack = props.onTrack,
    onChange = props.onChange;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    visible = _useState2[0],
    setVisible = _useState2[1];
  var straw = useSingleton(function () {
    return new ColorStraw();
  });
  var onTrackCallback = useRefCallback(onTrack);
  var onLoadClick = useCallback(function () {
    return straw.load();
  }, []);
  var changeHandler = useRefCallback(function (val) {
    onChange === null || onChange === void 0 ? void 0 : onChange(val);
  });
  useSafeInit(function () {
    var onTrackInfo = function onTrackInfo(state) {
      onTrackCallback(state);
      if (state === ColorStrawOperate.FAILED) {
        setVisible(true);
      }
    };
    straw.addListener('track', onTrackInfo);
    straw.addListener('change', onChange);
    return function () {
      straw.removeListener('track', onTrackInfo);
      straw.removeListener('change', onChange);
    };
  });
  var onClose = useCallback(function () {
    setVisible(false);
  }, []);
  var onSubmit = useCallback(function () {
    window.open('https://chrome.google.com/webstore/detail/%E8%B9%8A%E5%BE%84color/cjpfdcohegieleppnkadalkijenkhifl');
  }, []);
  var panelPrefix = usePrefixCls('color-picker-panel');
  var plugModalPrefix = usePrefixCls('color-picker-panel-plug-modal');
  var panelStyle = useMemo(function () {
    return {
      width: width
    };
  }, [width]);
  var controlsCls = usePrefixCls('color-picker-panel-controls', {
    'enable-alpha': enableAlpha
  });
  return React.createElement("div", {
    style: panelStyle,
    className: "".concat(panelPrefix, " ").concat(className)
  }, React.createElement("div", {
    className: "".concat(panelPrefix, "-saturation")
  }, React.createElement(Saturation, {
    className: "".concat(panelPrefix, "-saturation-component"),
    hsl: hsl,
    hsv: hsv,
    onChange: changeHandler
  })), React.createElement("div", {
    className: controlsCls
  }, enableChromePlugin && React.createElement("div", {
    className: "".concat(panelPrefix, "-straw"),
    onClick: onLoadClick
  }), React.createElement("div", {
    className: "".concat(panelPrefix, "-sliders")
  }, React.createElement("div", {
    className: "".concat(panelPrefix, "-hue")
  }, React.createElement(Hue, {
    className: "".concat(panelPrefix, "-hue-component"),
    onChange: changeHandler,
    hsl: hsl
  })), enableAlpha && React.createElement("div", {
    className: "".concat(panelPrefix, "-alpha")
  }, React.createElement(Alpha, {
    className: "".concat(panelPrefix, "-alpha-component"),
    onChange: changeHandler,
    rgb: rgb,
    hsl: hsl
  }))), React.createElement("div", {
    className: "".concat(panelPrefix, "-color")
  }, React.createElement(Checkboard, null), React.createElement("div", {
    className: "".concat(panelPrefix, "-active-color"),
    style: {
      background: "rgba(".concat(rgb === null || rgb === void 0 ? void 0 : rgb.r, ", ").concat(rgb === null || rgb === void 0 ? void 0 : rgb.g, ", ").concat(rgb === null || rgb === void 0 ? void 0 : rgb.b, ", ").concat((rgb === null || rgb === void 0 ? void 0 : rgb.a) || 1, ")")
    }
  }))), React.createElement("div", {
    className: "".concat(panelPrefix, "-sketch-fields")
  }, React.createElement(SketchFields, {
    rgb: rgb,
    hsl: hsl,
    hex: hex,
    onChange: changeHandler,
    disableAlpha: !enableAlpha
  })), React.createElement(SketchPresetColors, {
    colors: presetColors,
    onClick: changeHandler,
    onSwatchHover: onSwatchHover
  }), React.createElement(Modal, {
    show: visible,
    onOk: onSubmit,
    onCancel: onClose,
    okText: "\u6DFB\u52A0\u81F3Chrome\u6D4F\u89C8\u5668",
    cancelText: "\u6682\u4E0D\u9700\u8981",
    title: "\u6DFB\u52A0\u53D6\u8272\u5668\u63D2\u4EF6",
    maskClosable: false
  }, React.createElement("div", {
    className: plugModalPrefix
  }, React.createElement("p", {
    className: "".concat(plugModalPrefix, "-desc")
  }, "\u201C\u53D6\u8272\u5668\u201D\u80FD\u5438\u53D6\u9875\u9762\u5185\u4EFB\u610F\u989C\u8272\uFF0C\u5E2E\u52A9\u63D0\u5347\u914D\u8272\u6548\u7387\uFF1B\u9996\u6B21\u4F7F\u7528\uFF0C\u9700\u524D\u5F80Chrome\u6D4F\u89C8\u5668\u6DFB\u52A0\u201C\u53D6\u8272\u5668\u201D\u63D2\u4EF6\uFF0C\u6B65\u9AA4\u5982\u4E0B\uFF1A"), React.createElement("p", {
    className: "".concat(plugModalPrefix, "-step")
  }, "\u6B65\u9AA4\u4E00\uFF1A\u524D\u5F80Chrome\u6D4F\u89C8\u5668\u70B9\u51FB\u6DFB\u52A0\u201C\u53D6\u8272\u5668\u201D\u63D2\u4EF6\uFF1B"), React.createElement("div", {
    className: "".concat(plugModalPrefix, "-step-img")
  }), React.createElement("p", {
    className: "".concat(plugModalPrefix, "-step")
  }, "\u6B65\u9AA4\u4E8C\uFF1A\u5B8C\u6210\u6DFB\u52A0\u540E\uFF0C\u5173\u95EDChrome\u6D4F\u89C8\u5668\uFF0C\u91CD\u65B0\u6253\u5F00\u843D\u5730\u9875\u5373\u53EF\u4F7F\u7528\u201C\u53D6\u8272\u5668\u201D\u529F\u80FD\u3002"))));
});
export var ColorPickerPanel = function ColorPickerPanel(props) {
  var onChange = props.onChange,
    onValueChange = props.onValueChange,
    value = props.value,
    extra = _objectWithoutProperties(props, _excluded);
  return React.createElement(ColorPickerPanelImpl, _objectSpread({
    color: value,
    onChange: onChange,
    onChangeComplete: onValueChange
  }, extra));
};